import { useState, useRef, useEffect } from 'react';
import { cn, inventoryColorClass } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { MobileMetric } from '@/components/MobileMetric';
import { useInventory, useUpdateInventory } from '@/hooks/useInventory';
import { usePagination } from '@/hooks/usePagination';
import type { InventoryLevel } from '@/types';

function EditableQuantity({
  level,
  onSave,
  isPending,
}: {
  level: InventoryLevel;
  onSave: (inventoryItemId: number, available: number, locationId: number) => void;
  isPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    setValue(String(level.available));
    setEditing(true);
  };

  const save = () => {
    setEditing(false);
    const newQty = parseInt(value.trim(), 10);
    if (isNaN(newQty) || newQty === level.available) return;
    onSave(level.shopify_inventory_item_id, newQty, level.shopify_location_id);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-9 w-20 rounded border border-ring bg-background px-2 text-base font-mono text-foreground outline-none sm:h-7 sm:w-16 sm:text-sm"
      />
    );
  }

  return (
    <button
      onClick={startEdit}
      disabled={isPending}
      className={cn(
        'min-h-9 rounded px-2 py-0.5 text-base font-mono transition-colors hover:bg-accent sm:min-h-0 sm:text-sm',
        inventoryColorClass(level.available),
        isPending && 'opacity-50',
      )}
    >
      {level.available}
    </button>
  );
}

function InventoryMobileCard({
  item,
  onSave,
  isPending,
}: {
  item: InventoryLevel;
  onSave: (inventoryItemId: number, available: number, locationId: number) => void;
  isPending: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-medium text-foreground">
          {item.product_title ?? 'No product title'}
        </p>
        {item.variant_title && (
          <p className="mt-0.5 text-xs text-muted-foreground">{item.variant_title}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MobileMetric label="Available">
          <EditableQuantity level={item} onSave={onSave} isPending={isPending} />
        </MobileMetric>
        <MobileMetric label="Location">
          <p className="truncate text-sm text-foreground">
            {item.location_name ?? `Loc ${item.shopify_location_id}`}
          </p>
        </MobileMetric>
      </div>

      <p className="truncate text-xs font-mono text-muted-foreground">
        SKU {item.sku ?? '—'}
      </p>
    </div>
  );
}

export function InventoryPage() {
  const { page, perPage, search, sort, setPage, setSearch, setSort } = usePagination({
    initialSort: { field: 'product_title', direction: 'asc' },
  });

  const { data, isLoading, isFetching } = useInventory({
    page,
    per_page: perPage,
    search,
    sort_by: sort.field,
    sort_dir: sort.direction,
  });

  const updateMutation = useUpdateInventory();

  const handleSave = (inventoryItemId: number, available: number, locationId: number) => {
    updateMutation.mutate(
      { inventoryItemId, available, locationId },
      {
        onSuccess: () => toast.success('Inventory updated in Shopify'),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
      },
    );
  };

  const columns: DataTableColumn<InventoryLevel>[] = [
    {
      key: 'product_title',
      label: 'Product',
      sortable: true,
      render: (item) => (
        <div>
          <p className="text-sm text-foreground">{item.product_title ?? '\u2014'}</p>
          {item.variant_title && (
            <p className="text-xs text-muted-foreground">{item.variant_title}</p>
          )}
        </div>
      ),
    },
    {
      key: 'sku',
      label: 'SKU',
      sortable: true,
      className: 'w-[120px]',
      render: (item) => (
        <span className="text-xs text-muted-foreground font-mono">{item.sku ?? '\u2014'}</span>
      ),
    },
    {
      key: 'location_name',
      label: 'Location',
      sortable: true,
      className: 'w-[160px]',
      render: (item) => (
        <span className="text-sm text-muted-foreground">
          {item.location_name ?? `Loc ${item.shopify_location_id}`}
        </span>
      ),
    },
    {
      key: 'available',
      label: 'Available',
      sortable: true,
      className: 'w-[100px]',
      render: (item) => (
        <EditableQuantity
          level={item}
          onSave={handleSave}
          isPending={updateMutation.isPending}
        />
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.total ?? 0}
        page={page}
        perPage={perPage}
        sort={sort}
        search={search}
        isLoading={isLoading}
        isFetching={isFetching}
        getRowId={(item) => item.id}
        onPageChange={setPage}
        onSortChange={setSort}
        onSearchChange={setSearch}
        searchPlaceholder="Search by product name, SKU..."
        emptyMessage="No inventory levels found"
        emptySubMessage="Sync your Shopify inventory to see data"
        mobileRender={(item) => (
          <InventoryMobileCard
            item={item}
            onSave={handleSave}
            isPending={updateMutation.isPending}
          />
        )}
      />
    </div>
  );
}
